import { AxiosResponse } from 'axios';
import {
  call,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';

import { depreciationClientNotificationMessages } from '../domain/depreciation/clientNotification';
import { DataDepreciationResponse } from '../domain/depreciation/types';

import {
  DataDepreciationActionCreators,
  DataDepreciationActionTypes,
  FetchDataDepreciationAction,
  dataDepreciationDataSelector,
} from '../redux/server/dataDepreciation';
import {
  SettingsOptionsKiActionCreators,
  SettingsOptionsKiActionTypes,
  settingsOptionsKiLoadingSelector,
  settingsOptionsKiSearchConditionSelector,
} from '../redux/server/settingsOptionsKi';
import { ClientNotificationActionCreators } from '../redux/ui/clientNotification';
import {
  DepreciationSettingActionCreators,
  depreciationTableHeaderSubMenuMultiFilterSelector,
} from '../redux/ui/depreciationSetting';
import { Api, buildConfig } from '../utils/api';
import {
  DEPRECIATION_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER,
  buildContributionWeekMultiFilterSelectOptions,
  buildTypeMultiFilterSelectOptions,
  collectUniqueColumnValues,
  selectedValuesWithinOptions,
} from '../utils/tableHeaderSubMenuMultiFilter';
import { handleErrorSaga } from './errorSaga';

/**
 * dataDepreciationのデータを取得する
 */
export function* fetchDataDepreciationSaga(
  api: Api,
  action: FetchDataDepreciationAction
) {
  try {
    yield put(
      DataDepreciationActionCreators.fetchDataDepreciationRequestAction()
    );

    const response: AxiosResponse<DataDepreciationResponse> = yield call(
      api.get,
      '/data/depreciation',
      buildConfig(action.payload.params)
    );

    const dataDepreciation = response.data;

    yield put(
      DataDepreciationActionCreators.fetchDataDepreciationSuccessAction(
        dataDepreciation
      )
    );

    const kiOptionsLoadingState: ReturnType<
      typeof settingsOptionsKiLoadingSelector
    > = yield select(settingsOptionsKiLoadingSelector);
    if (kiOptionsLoadingState !== 'loaded') {
      if (kiOptionsLoadingState !== 'loading') {
        yield put(
          SettingsOptionsKiActionCreators.fetchSettingsOptionsKiAction()
        );
      }
      yield race({
        success: take(
          SettingsOptionsKiActionTypes.FETCH_SETTINGS_OPTIONS_KI_SUCCESS
        ),
        reset: take(SettingsOptionsKiActionTypes.RENEW_SETTINGS_OPTIONS_KI),
      });
    }

    const kiSearchCondition: ReturnType<
      typeof settingsOptionsKiSearchConditionSelector
    > = yield select(settingsOptionsKiSearchConditionSelector);
    const sisTypeNames = kiSearchCondition.sisTypes.map((item) => item.name);
    const uniqueTypeValues = collectUniqueColumnValues(
      [
        {
          columns: dataDepreciation.data.columns,
          rows: dataDepreciation.data.rows,
        },
      ],
      'type'
    );
    const typeOptions =
      sisTypeNames.length > 0
        ? buildTypeMultiFilterSelectOptions(sisTypeNames, uniqueTypeValues)
        : uniqueTypeValues.filter((value) => value !== '-');

    const contributionOptions =
      DEPRECIATION_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER.reduce<
        Record<string, string[]>
      >((optionsByColumn, columnCode) => {
        const columnIndex = dataDepreciation.data.columns.findIndex(
          (column) => column.code === columnCode
        );
        const values =
          columnIndex < 0
            ? []
            : dataDepreciation.data.rows.map(
                (row) => row.data.at(columnIndex)?.value
              );

        return {
          ...optionsByColumn,
          [columnCode]: buildContributionWeekMultiFilterSelectOptions(values),
        };
      }, {});

    yield put(
      DepreciationSettingActionCreators.selectDepreciationTableHeaderSubMenuMultiFilterSelectOptionsAction(
        { type: typeOptions, ...contributionOptions }
      )
    );

    const currentFilter: ReturnType<
      typeof depreciationTableHeaderSubMenuMultiFilterSelector
    > = yield select(depreciationTableHeaderSubMenuMultiFilterSelector);
    const selectedType = currentFilter?.type ?? [];
    const nextType = selectedValuesWithinOptions(selectedType, typeOptions);
    if (selectedType.length !== nextType.length) {
      yield put(
        DepreciationSettingActionCreators.selectDepreciationTableHeaderSubMenuMultiFilterAction(
          { ...(currentFilter ?? {}), type: nextType }
        )
      );
    }
  } catch (error: unknown) {
    yield put(DataDepreciationActionCreators.renewDataDepreciationAction());
    yield fork(handleErrorSaga, error);
  }
}

function* handleFetchDataDepreciationSaga(api: Api) {
  yield takeEvery(
    DataDepreciationActionTypes.FETCH_DATA_DEPRECIATION,
    fetchDataDepreciationSaga,
    api
  );
}

function* validateDataDepreciationSaga() {
  const data: DataDepreciationResponse | undefined = yield select(
    dataDepreciationDataSelector
  );
  if (data?.data.rows.length === 0 || data === undefined) {
    yield put(
      ClientNotificationActionCreators.setClientNotification(
        depreciationClientNotificationMessages
      )
    );
  }
}

function* handleFetchSuccessSaga() {
  yield takeEvery(
    DataDepreciationActionTypes.FETCH_DATA_DEPRECIATION_SUCCESS,
    validateDataDepreciationSaga
  );
}

export function* dataDepreciationSaga(context: { api: Api }) {
  yield fork(handleFetchDataDepreciationSaga, context.api);
  yield fork(handleFetchSuccessSaga);
}
